import dailyClosingService from '../../services/dailyClosingService';
import { readBusinessPayloadFieldsSync } from '../../application/read/syncBusinessPayloadRead';
import { getCurrentBusinessId } from '../../utils/sharedCore';
import { flushPendingStorageWrites } from '../../infrastructure/storage/businessPayloadWrite';
import { runAutomaticPeriodClosingCheck } from './periodClosingOrchestrator';
import { readStockSnapshotsSyncForBusiness } from '../inventory/snapshots';
import { getInventoryTotalValue } from '../inventory/valuation';

/** Fired when lazy boot phase 2 finished — deferred domains (sales, expenses, …) are in KV. */
export const BUSINESS_LAZY_LOAD_COMPLETE_EVENT = 'aman:businessLazyLoadComplete';

export function dispatchBusinessLazyLoadComplete(businessId) {
  if (typeof window === 'undefined' || !businessId) return;
  window.dispatchEvent(
    new CustomEvent(BUSINESS_LAZY_LOAD_COMPLETE_EVENT, { detail: { businessId } })
  );
}

let closingRunPromise = null;
let getClosingDataProvider = null;

export function buildDailyClosingPayload({
  sales,
  purchases,
  expenses,
  supermarketPayments,
  invoicePayments,
  businessInfo,
  workerSessions,
  discrepancyManagement,
  supplierDebts,
  customerDebts,
  inventoryData,
  products,
  lots,
  expiredProducts,
  supermarketInvoices,
  stockSnapshots = null
}) {
  return {
    sales: sales || [],
    purchases: purchases || [],
    expenses: expenses || [],
    supermarketPayments: supermarketPayments || [],
    invoicePayments: invoicePayments || [],
    cashRegister: businessInfo?.cashRegister || null,
    sessions: workerSessions || [],
    ecarts: [],
    discrepancyManagement: discrepancyManagement || {},
    supplierDebts: supplierDebts || [],
    customerDebts: customerDebts || [],
    inventoryValue: inventoryData?.inventoryValue?.totalValue ?? null,
    products: products || [],
    lots: lots || [],
    expiredProducts: expiredProducts || [],
    supermarketInvoices: supermarketInvoices || [],
    bankAccounts: businessInfo?.bankAccounts || [],
    stockSnapshots: stockSnapshots ?? null
  };
}

/**
 * Authoritative closing payload from KV / entity stores (not React state).
 * Safe at startup before lazy phase 2 and on app quit.
 */
export function buildDailyClosingPayloadFromStorage(businessId) {
  const fields = readBusinessPayloadFieldsSync(businessId);
  let inventoryValue =
    fields.stockData?.inventoryValue?.totalValue ??
    fields.reports?.inventoryValue?.totalValue ??
    null;

  const stockSnapshots = readStockSnapshotsSyncForBusiness(businessId);
  if (inventoryValue == null && stockSnapshots?.length) {
    inventoryValue = getInventoryTotalValue({
      products: fields.products || [],
      lots: fields.lots || [],
      stockSnapshots
    });
  }

  return buildDailyClosingPayload({
    ...fields,
    stockSnapshots,
    inventoryData:
      inventoryValue != null ? { inventoryValue: { totalValue: inventoryValue } } : {}
  });
}

export function registerDailyClosingDataProvider(getClosingData) {
  getClosingDataProvider = typeof getClosingData === 'function' ? getClosingData : null;
}

/**
 * Canonical automatic-closing payload = KV/entity storage.
 * React state is only used as a fill-in when storage lists are still empty
 * (e.g. mid lazy-boot) — never preferred over non-empty storage.
 */
export async function resolveAutomaticClosingPayload() {
  const businessId = getCurrentBusinessId({ allowFallback: false });
  if (!businessId) return null;

  let storagePayload;
  try {
    storagePayload = buildDailyClosingPayloadFromStorage(businessId);
  } catch (error) {
    console.warn(
      '[DAILY CLOSING] Storage payload failed, trying React provider:',
      error?.message || error
    );
    storagePayload = null;
  }

  const storageHasActivity =
    (storagePayload?.sales?.length || 0) > 0 ||
    (storagePayload?.purchases?.length || 0) > 0 ||
    (storagePayload?.expenses?.length || 0) > 0;

  if (storageHasActivity) {
    return storagePayload;
  }

  if (getClosingDataProvider) {
    try {
      const reactPayload = buildDailyClosingPayload(getClosingDataProvider());
      const reactHasActivity =
        (reactPayload.sales?.length || 0) > 0 ||
        (reactPayload.purchases?.length || 0) > 0 ||
        (reactPayload.expenses?.length || 0) > 0;
      if (reactHasActivity) {
        return reactPayload;
      }
    } catch {
      /* keep storage (possibly empty) */
    }
  }

  return storagePayload || buildDailyClosingPayloadFromStorage(businessId);
}

/**
 * Serialised automatic closing — never runs in parallel.
 * @param {Object|null} [closingData] — optional; defaults to KV read
 */
export async function runAutomaticDailyClosingCheck(closingData = null) {
  if (closingRunPromise) {
    return closingRunPromise;
  }

  closingRunPromise = (async () => {
    try {
      const payload = closingData || (await resolveAutomaticClosingPayload());
      if (!payload) {
        return { processed: [], errors: ['No business or closing data'] };
      }

      const result = await dailyClosingService.checkAndPerformAutomaticClosings(payload, 'system');

      if (result.processed?.length > 0) {
        console.log(
          `✅ [DAILY CLOSING] Automatischer Abschluss durchgeführt für ${result.processed.length} Tag(e)` +
            (result.lookbackDays ? ` (Fenster ${result.lookbackDays}d)` : '')
        );
        await flushPendingStorageWrites();
      }

      if (result.errors?.length > 0) {
        console.warn('⚠️ [DAILY CLOSING] Fehler beim automatischen Abschluss:', result.errors);
      }

      if (result.skippedBeyondWindow?.length > 0) {
        console.warn(
          '⚠️ [DAILY CLOSING] Aktivität außerhalb des Nachhol-Fensters:',
          result.skippedBeyondWindow.slice(0, 5)
        );
      }

      // After daily catch-up: once-per-period week/month/quarter/year (idempotent)
      let periodResult = { processed: [], errors: [], skipped: [] };
      try {
        periodResult = await runAutomaticPeriodClosingCheck('system');
      } catch (periodError) {
        console.warn(
          '⚠️ [PERIOD CLOSING] Nach Tagesabschluss fehlgeschlagen:',
          periodError?.message || periodError
        );
      }

      return { ...result, periodClosing: periodResult };
    } finally {
      closingRunPromise = null;
    }
  })();

  return closingRunPromise;
}

/** Schedule check at local 00:00:05 (avoids edge race at exact midnight). */
export function scheduleLocalMidnightDailyClosing(onTick) {
  let timerId = null;

  const scheduleNext = () => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5, 0);
    const delay = Math.max(500, next.getTime() - now.getTime());
    timerId = setTimeout(() => {
      void onTick();
      scheduleNext();
    }, delay);
    timerId.unref?.();
  };

  scheduleNext();
  return () => {
    if (timerId) clearTimeout(timerId);
  };
}

/** Run catch-up closing before Electron quit (must finish within main-process timeout). */
export async function runDailyClosingBeforeQuit({ timeoutMs = 1800 } = {}) {
  const businessId = getCurrentBusinessId({ allowFallback: false });
  if (!businessId) {
    return { skipped: true, reason: 'no_business' };
  }

  let timer;
  try {
    const work = runAutomaticDailyClosingCheck();
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ skipped: true, reason: 'timeout' }), timeoutMs);
    });
    return await Promise.race([work, timeout]);
  } catch (error) {
    console.warn('[DAILY CLOSING] Quit-time check failed:', error?.message || error);
    return { skipped: true, reason: 'error', error: error?.message || String(error) };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Manueller Tagesabschluss (Manager) — standardmäßig heute.
 */
export async function performManualDailyClosing(
  rawData,
  { date = null, closedBy = 'manager', businessId = null } = {}
) {
  const payload = buildDailyClosingPayload(rawData);
  const closingDate = date ? new Date(date) : new Date();
  return dailyClosingService.performClosing(closingDate, closedBy, {
    ...payload,
    closingDate,
    businessId: businessId || rawData.businessId || null
  }, { persistImmediately: true });
}

export const DAILY_CLOSING_SAFETY_INTERVAL_MS = 3600000;
export const PERIODIC_SYNC_INTERVAL_MS = 60000;
export const BUSINESS_STATUS_INTERVAL_MS = 120000;
